import type { AircraftSpec, CompanyProfile } from "./types";

export type RadioCompany = Pick<CompanyProfile, "useTelephonyCallsign" | "telephonyName">;

/** Tail registration as radio / ATC id (C-GVXL, N123AB). */
export function normalizeRegistration(raw: string): string {
  return raw.trim().toUpperCase().replace(/\s+/g, "");
}

const NATO_LETTER: Record<string, string> = {
  ALPHA: "A",
  BRAVO: "B",
  CHARLIE: "C",
  DELTA: "D",
  ECHO: "E",
  FOXTROT: "F",
  GOLF: "G",
  HOTEL: "H",
  INDIA: "I",
  JULIETT: "J",
  JULIET: "J",
  KILO: "K",
  LIMA: "L",
  MIKE: "M",
  NOVEMBER: "N",
  OSCAR: "O",
  PAPA: "P",
  QUEBEC: "Q",
  ROMEO: "R",
  SIERRA: "S",
  TANGO: "T",
  UNIFORM: "U",
  VICTOR: "V",
  WHISKEY: "W",
  "X-RAY": "X",
  XRAY: "X",
  YANKEE: "Y",
  ZULU: "Z",
};

function compactCallsignLetters(raw: string): string {
  const words = raw
    .trim()
    .split(/[\s,/]+/)
    .map((word) => word.replace(/[.,]/g, "").toUpperCase())
    .filter(Boolean);
  if (words.length >= 3 && words.every((word) => word in NATO_LETTER)) {
    return words.map((word) => NATO_LETTER[word] ?? "").join("");
  }
  return normalizeRegistration(raw).replace(/-/g, "");
}

/**
 * Map hover id: hyphenated ICAO-style tail (`C-GVXL`), never NATO phonetic.
 * Flight numbers (`CEL1047`) and US `N` numbers stay compact.
 */
export function formatMapCallsign(raw?: string | null): string {
  const trimmed = typeof raw === "string" ? raw.trim() : "";
  if (!trimmed) return "";
  const compact = compactCallsignLetters(trimmed);
  if (!compact) return "";
  if (/^N[0-9A-Z]+$/.test(compact)) return compact;
  if (/^[A-Z]{2,3}\d{2,4}[A-Z]?$/.test(compact)) return compact;
  if (compact.length === 5 && /^[A-Z][A-Z0-9]{4}$/.test(compact)) {
    return `${compact[0]}-${compact.slice(1)}`;
  }
  if (compact.length === 5 && /^[A-Z]{2}[A-Z0-9]{3}$/.test(compact)) {
    return `${compact.slice(0, 2)}-${compact.slice(2)}`;
  }
  const normalized = normalizeRegistration(trimmed);
  return normalized.includes("-") ? normalized : compact;
}

export function telephonyPrefix(company: RadioCompany | null | undefined, spec: Pick<AircraftSpec, "callsignPrefix">): string {
  const spoken = company?.telephonyName?.trim().toUpperCase().replace(/[^A-Z0-9]/g, "") ?? "";
  if (spoken) return spoken.slice(0, 7);
  return (spec.callsignPrefix || "OPS").toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 7) || "OPS";
}

/**
 * Primary radio id is the aircraft registration.
 * Company telephony is opt-in only (`useTelephonyCallsign`).
 */
export function radioIdFor(
  spec: Pick<AircraftSpec, "registration" | "callsignPrefix" | "id" | "icaoType">,
  company?: RadioCompany | null,
): string {
  if (company?.useTelephonyCallsign) {
    const prefix = telephonyPrefix(company, spec);
    const n =
      spec.icaoType === "PC12" || spec.id === "pc12-ng"
        ? 10 + Math.floor(Math.random() * 20)
        : 80 + Math.floor(Math.random() * 19);
    return `${prefix}${n}`;
  }
  return normalizeRegistration(spec.registration);
}
